/**
 * Member helpers shared by the memo surfaces: resolving an org member's
 * display name, initials and avatar URL, filtering the assignable set, and
 * naming a memo's owner / creator from the org roster.
 *
 * Deliberately member-shaped, not note-shaped — MemoOwnerPicker and the task
 * surfaces' OrgMemberPicker both read these so their rows can't drift. Memo
 * *record* helpers (association names, privacy, due) live in `notes.utils.ts`.
 */

import type { Membership } from 'api-client';

import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

import { isAllowedUrl } from '../../utils/urlValidation';
import type { Note } from './notes.types';

/** Resolve a member's display name with a consistent fallback chain:
 *  displayName → "firstName lastName" → email. Returns undefined when none
 *  of those produce a non-empty string. */
export function resolveMemberName(member: Membership): string | undefined {
  const display = member.displayName?.trim();
  if (display) {
    return display;
  }
  const fullName = `${member.firstName || ''} ${member.lastName || ''}`.trim();
  if (fullName) {
    return fullName;
  }
  return member.email || undefined;
}

export function getMemoOwnerName(
  note: Note,
  orgUsers: Membership[]
): string | undefined {
  if (!note.owner_entity_id) {
    return undefined;
  }
  const owner = orgUsers.find(u => u.entityId === note.owner_entity_id);
  return owner ? resolveMemberName(owner) : undefined;
}

export function getMemoCreatorName(
  note: Note,
  orgUsers: Membership[]
): string | undefined {
  if (!note.creator_entity_id) {
    return undefined;
  }
  const creator = orgUsers.find(u => u.entityId === note.creator_entity_id);
  return creator ? resolveMemberName(creator) : undefined;
}

/** Members eligible as task assignees/owners: active, non-partner, non-viewer,
 *  with an entity id. Shared by MemberPicker and OrgMemberPicker so a rule
 *  change can't diverge between the two pickers. */
export function filterAssignableMembers(members: Membership[]): Membership[] {
  return members.filter(
    m =>
      !!m.entityId &&
      m.deactivatedAt == null &&
      m.memberKind !== 'partner' &&
      m.memberKind !== 'viewer'
  );
}

/**
 * First letter of `displayName` (or `firstName`) — falls back to "?". Shared
 * by MemoOwnerPicker and OrgMemberPicker so their avatar initials can
 * never drift from each other.
 */
export function deriveInitials(member: Membership): string {
  const source =
    member.displayName?.trim() ||
    `${member.firstName ?? ''} ${member.lastName ?? ''}`.trim() ||
    member.email ||
    '?';
  const [first = '', second = ''] = source.split(/\s+/);
  return `${first[0] ?? ''}${second[0] ?? ''}`.toUpperCase() || '?';
}

/**
 * Pretty display name for a member (used in popover rows + tooltips). Shared
 * by MemoOwnerPicker and OrgMemberPicker — see `deriveInitials`.
 */
export function memberDisplayName(member: Membership): string {
  return (
    member.displayName?.trim() ||
    `${member.firstName ?? ''} ${member.lastName ?? ''}`.trim() ||
    member.email
  );
}

/**
 * Resolve a member's profile-photo URL from their CRM object, falling back
 * through the same priority order as `CrmObjectAvatar`:
 *   1. `ahoy_profile_photo_url`
 *   2. `ahoy_avatar_url`
 *   3. (none — caller renders initials)
 * Shared by MemoOwnerPicker and OrgMemberPicker — see `deriveInitials`.
 *
 * Each candidate is validated with `isAllowedUrl` (http/https only) before
 * being returned, so a malformed or `javascript:`/`data:` value stored on the
 * CRM object can never reach an `<img src>`. A rejected candidate falls
 * through to the next in the priority order (or `undefined`).
 */
export function getMemberAvatarSrc(
  crmObject: CrmObject | undefined
): string | undefined {
  if (!crmObject) {
    return undefined;
  }
  const profilePhoto = getPropertyValue<string>(
    crmObject.properties?.ahoy_profile_photo_url
  );
  if (profilePhoto && isAllowedUrl(profilePhoto)) {
    return profilePhoto;
  }
  const avatarUrl = getPropertyValue<string>(
    crmObject.properties?.ahoy_avatar_url
  );
  if (avatarUrl && isAllowedUrl(avatarUrl)) {
    return avatarUrl;
  }
  return undefined;
}
